import type { TtsProviderId } from '@orborix/shared-types';
import type { PersonaDto, PersonaGender } from '../../lib/personasApi.js';

/**
 * Modelo de tarjeta del CoachPicker (V2). La DB de personas NO trae
 * `color`/`flag`/`blurb`/`initial` que el diseño dibuja, así que los derivamos
 * client-side del catálogo real (`GET /api/personas` filtrado por
 * `category === 'english'` y por el provider TTS activo). El grid se adapta al N real — NO asume 10 voces.
 */
export interface CoachCardModel {
  id: string;
  name: string;
  voiceId: string;
  initial: string;
  /** Acento normalizado (filtro por acento del picker). */
  accentKey: CoachAccentKey;
  /** Bandera derivada del acento (US 🇺🇸 / UK 🇬🇧 / AU 🇦🇺 / 🌐 por defecto). */
  flag: string;
  /** Acento corto (EN-US / EN-UK / EN-AU / EN). */
  tag: string;
  /** Acento legible es-first ("Americano" / "Británico" / "Australiano"). */
  region: string;
  /** Descriptor de una línea de la persona ("Cálida y acogedora"). */
  blurb: string;
  /** "Mujer" / "Hombre" (es-first); `null` si el género no está verificado. */
  genderLabel: string | null;
  /** Color de paleta asignado por índice (vinilo del avatar). */
  color: string;
}

export type UiLocale = 'es' | 'en';

/**
 * Etiqueta del género de la voz. Español primero (la UI de speak es es-first);
 * el inglés queda listo para cuando haya locale de UI.
 */
const GENDER_LABELS: Record<UiLocale, Record<PersonaGender, string>> = {
  es: { female: 'Mujer', male: 'Hombre', neutral: 'Neutra' },
  en: { female: 'Woman', male: 'Man', neutral: 'Neutral' },
};

/** `null` = género desconocido: no se muestra etiqueta (nunca se adivina). */
export function genderLabel(gender: PersonaGender | null, locale: UiLocale = 'es'): string | null {
  return gender ? GENDER_LABELS[locale][gender] : null;
}

/** Paleta del prototipo, ciclada por índice. */
const COACH_PALETTE: ReadonlyArray<string> = [
  '#ffb380',
  '#7cc4ff',
  '#d4f23e',
  '#ffd166',
  '#ff6ec7',
  '#5ce1e6',
  '#a78bfa',
  '#ff9d6c',
  '#8095ff',
  '#6df0a8',
];

/**
 * Acento normalizado de la persona (`personas.accent`: US / UK|GB / AU).
 * `OTHER` = sin acento reconocido → 🌐 "Internacional" (personas custom).
 */
export type CoachAccentKey = 'US' | 'UK' | 'AU' | 'OTHER';

export function accentKeyFor(accent: string | null): CoachAccentKey {
  const a = (accent ?? '').toUpperCase();
  if (a === 'US') return 'US';
  if (a === 'UK' || a === 'GB') return 'UK';
  if (a === 'AU') return 'AU';
  return 'OTHER';
}

/** Bandera, tag del deck y región legible (es-first) por acento. */
export const ACCENT_DISPLAY: Record<CoachAccentKey, { flag: string; tag: string; region: string }> = {
  US: { flag: '🇺🇸', tag: 'EN-US', region: 'Americano' },
  UK: { flag: '🇬🇧', tag: 'EN-UK', region: 'Británico' },
  AU: { flag: '🇦🇺', tag: 'EN-AU', region: 'Australiano' },
  OTHER: { flag: '🌐', tag: 'EN', region: 'Internacional' },
};

/** Mapea una persona de inglés a su tarjeta de coach con campos derivados. */
export function toCoachCard(persona: PersonaDto, index: number): CoachCardModel {
  const accentKey = accentKeyFor(persona.accent);
  const display = ACCENT_DISPLAY[accentKey];
  return {
    id: persona.id,
    name: persona.name,
    voiceId: persona.voiceId,
    initial: (persona.name.trim()[0] ?? '?').toUpperCase(),
    accentKey,
    flag: display.flag,
    tag: display.tag,
    region: display.region,
    blurb: persona.description,
    genderLabel: genderLabel(persona.gender),
    color: COACH_PALETTE[index % COACH_PALETTE.length]!,
  };
}

/**
 * Filtra a personas de inglés CON VOZ DEL PROVIDER TTS ACTIVO y las mapea a
 * tarjetas (orden estable del backend). Con speak en Gemini se ven las personas
 * de voz Gemini; en ElevenLabs, las de siempre. Mostrar voces del otro provider
 * sería ofrecer algo que la sesión no puede usar (el backend caería a la voz
 * default del módulo).
 */
export function buildCoachCatalog(
  personas: ReadonlyArray<PersonaDto>,
  provider: TtsProviderId,
): CoachCardModel[] {
  return personas
    .filter((p) => p.category === 'english' && p.ttsProvider === provider)
    .map(toCoachCard);
}
